import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { homeApi, authApi } from '../api/client';
import { useAuthStore } from '../store';

// ---- Home screen rows -------------------------------------------------------

// Home rows are filtered server-side by the active profile's Kids/Adult type (and continue-watching
// is specific to the profile's own viewing history), but react-query only refetches on a queryKey
// change — so every Home query key includes the active profile's id to force a refetch (instead of
// serving another profile's cached rows) whenever the person switches profiles.
function useActiveProfileId() {
  return useAuthStore((s) => s.activeProfile?.Id ?? null);
}

export function useCarousel(limit = 10) {
  const profileId = useActiveProfileId();
  return useQuery({
    queryKey: ['home', 'carousel', limit, profileId],
    queryFn: async () => {
      const { data } = await homeApi.getCarousel(limit);
      if (!data.success) throw new Error('Failed to load carousel');
      return data.data;
    },
    // The server returns a fresh random selection on every call, so the result must not outlive the
    // visit: gcTime 0 drops it from the cache as soon as Home unmounts (the next visit fetches a new
    // random set instead of flashing last visit's slides and then swapping them), and staleTime
    // Infinity stops focus / reconnect / remount refetches from swapping slides mid-visit.
    staleTime: Infinity,
    gcTime: 0,
  });
}

export function useTop10() {
  const profileId = useActiveProfileId();
  return useQuery({
    queryKey: ['home', 'top10', profileId],
    queryFn: async () => {
      const { data } = await homeApi.getTop10();
      if (!data.success) throw new Error('Failed to load top 10');
      return data.data;
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useQuickLists() {
  const profileId = useActiveProfileId();
  return useQuery({
    queryKey: ['home', 'quickLists', profileId],
    queryFn: async () => {
      const { data } = await homeApi.getQuickLists();
      if (!data.success) throw new Error('Failed to load category quick lists');
      return data.data;
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useNewReleases(limit = 10) {
  const profileId = useActiveProfileId();
  return useQuery({
    queryKey: ['home', 'newReleases', limit, profileId],
    queryFn: async () => {
      const { data } = await homeApi.getNewReleases(limit);
      if (!data.success) throw new Error('Failed to load new releases');
      return data.data;
    },
    // Server randomizes the selection on every call — don't cache across visits/refocus.
    staleTime: 0,
    refetchOnMount: 'always',
  });
}

export function useContinueWatching(enabled = true) {
  const profileId = useActiveProfileId();
  return useQuery({
    queryKey: ['home', 'continueWatching', profileId],
    queryFn: async () => {
      const { data } = await homeApi.getContinueWatching();
      if (!data.success) throw new Error('Failed to load continue watching');
      return data.data;
    },
    enabled,
    staleTime: 60 * 1000,
  });
}

export function useUpdateWatchProgress() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ episodeId, positionSeconds, durationSeconds }) =>
      homeApi.updateWatchProgress(episodeId, positionSeconds, durationSeconds),
    onSuccess: () => qc.invalidateQueries(['home', 'continueWatching']),
  });
}

export function useTvSeries(page = 1, pageSize = 20) {
  const profileId = useActiveProfileId();
  return useQuery({
    queryKey: ['home', 'tvSeries', page, pageSize, profileId],
    queryFn: async () => {
      const { data } = await homeApi.getTvSeries(page, pageSize);
      if (!data.success) throw new Error('Failed to load TV series');
      return data;
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useComingSoon(limit = 10) {
  const profileId = useActiveProfileId();
  return useQuery({
    queryKey: ['home', 'comingSoon', limit, profileId],
    queryFn: async () => {
      const { data } = await homeApi.getComingSoon(limit);
      if (!data.success) throw new Error('Failed to load coming soon');
      return data.data;
    },
    staleTime: 5 * 60 * 1000,
  });
}

// ---- Auth: logout / delete account ------------------------------------------

export function useLogout() {
  const { logout: clearStore } = useAuthStore();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => authApi.logout(),
    onSettled: () => {
      // Clear client-side auth state regardless of whether the network call
      // succeeded — the user's intent is to be signed out either way.
      clearStore();
      qc.clear();
    },
  });
}

export function useDeleteAccount() {
  const { logout: clearStore } = useAuthStore();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (password) => authApi.deleteAccount(password),
    onSuccess: () => {
      clearStore();
      qc.clear();
    },
  });
}
